import {
  Customer as sdkCustomer,
  Site as sdkSite,
  Scheduler as sdkScheduler,
  Unit as sdkUnit
} from "coolremote-sdk";
import { Action, action, Thunk, thunk } from "easy-peasy";

interface ISchedulePayload {
  id: string;
  data: object;
}

interface IGetSchedulePayload {
  id: string | any;
  objectType: "unit" | "group" | "system";
  type?: undefined | string;
}

interface ICreateSchedulePayload {
  objId: string;
  objectType: "unit" | "group" | "system";
  data: any;
}

interface IBacNetSchedulePayload {
  bacnetId: string;
  type?: string;
}

interface ICreateBacNetSchedulePayload {
  bacnetId: string;
  data: any;
}

interface IModbusSchedulePayload {
  modbusId: string;
  type?: string;
}

interface ICreateModbusSchedulePayload {
  modbusId: string;
  data: any;
}

export interface IScheduleModel {
  schedules: any;
  getSchedule: Thunk<IScheduleModel, string, any>;
  getCustomerSchedules: Thunk<
    IScheduleModel,
    string | null | { id: any; type: any }
  >;
  getUnitSchedules: Thunk<
    IScheduleModel,
    { id: string; type: number | undefined }
  >;
  deleteSchedule: Thunk<IScheduleModel, string, any>;
  updateSchedule: Thunk<IScheduleModel, ISchedulePayload, any>;
  removeSchedule: Action<IScheduleModel, any>;
  getObjectSchedules: Thunk<IScheduleModel, IGetSchedulePayload, any>;
  createObjectSchedules: Thunk<IScheduleModel, ICreateSchedulePayload, any>;
  createUnitsSchedule: Thunk<IScheduleModel, any>;
  getSiteSetbackSchedule: Thunk<
    IScheduleModel,
    string | null | { id: any; type: any }
  >;
  getSiteUnifiedSchedule: Thunk<IScheduleModel, string>;
  updateSetbackSchedule: Thunk<IScheduleModel, ISchedulePayload, any>;
  createSetbackSchedule: Thunk<IScheduleModel, any>;
  createUnifiedSchedule: Thunk<IScheduleModel, any>;
  deleteUnitFromSechdule: Thunk<
    IScheduleModel,
    { scheduleId: string; unitId: string },
    any
  >;
  deleteGroupFromSechdule: Thunk<
    IScheduleModel,
    { scheduleId: string; groupId: string },
    string,
    any
  >;
  getBacNetSchedules: Thunk<IScheduleModel, IBacNetSchedulePayload, any>;
  createBacNetSchedule: Thunk<IScheduleModel, ICreateBacNetSchedulePayload, any>;
  updateBacNetSchedule: Thunk<IScheduleModel, ISchedulePayload, any>;
  deleteBacNetSchedule: Thunk<IScheduleModel, string, any>;
  getModbusSchedules: Thunk<IScheduleModel, IModbusSchedulePayload, any>;
  createModbusSchedule: Thunk<IScheduleModel, ICreateModbusSchedulePayload, any>;
}

export const scheduleModel: IScheduleModel = {
  schedules: [],
  getSchedule: thunk((actions, payload) => {
    return sdkScheduler.getScheduleById(payload);
  }),
  getCustomerSchedules: thunk((actions, payload) => {
    return sdkCustomer.getSchedules(payload);
  }),
  getUnitSchedules: thunk((actions, payload) => {
    return sdkUnit.getSchedules(payload);
  }),
  getSiteSetbackSchedule: thunk((actions, payload) => {
    return sdkSite.getSiteSetbackSchedule(payload);
  }),
  updateSetbackSchedule: thunk((actions, payload) => {
    const { id, data } = payload;
    return sdkScheduler.updateSetbackSchedule(id, data);
  }),
  createSetbackSchedule: thunk((actions, payload) => {
    const { data } = payload;
    return sdkScheduler.createSetbackSchedule(data);
  }),
  getSiteUnifiedSchedule: thunk((actions, payload) => {
    return sdkSite.getSiteUnifiedSchedule(payload);
  }),
  createUnifiedSchedule: thunk((actions, payload) => {
    const { data } = payload;
    return sdkScheduler.createUnifiedSchedule(data);
  }),
  deleteUnitFromSechdule: thunk((actions, payload) => {
    return sdkScheduler.deleteUnitFromSechdule(payload);
  }),

  deleteGroupFromSechdule: thunk((actions, payload) => {
    return sdkScheduler.deleteGroupFromSechdule(payload);
  }),
  deleteSchedule: thunk((actions, payload) => {
    return sdkScheduler.delete(payload);
  }),
  removeSchedule: action((state, payload) => {
    const { schedules: newScedules } = state;
    delete newScedules[payload];

    state.schedules = newScedules;
  }),

  updateSchedule: thunk((actions, payload) => {
    const { id, data } = payload;

    return sdkScheduler.update(id, data);
  }),
  getObjectSchedules: thunk((actions, payload) => {
    const { id, objectType, type } = payload;
    return sdkScheduler.getObjectScheduleById(objectType, id, type);
  }),
  createObjectSchedules: thunk((actions, payload) => {
    const { objId, data, objectType } = payload;
    return sdkScheduler.createObjectSchedule(objectType, objId, data);
  }),
  createUnitsSchedule: thunk((actions, payload) => {
    return sdkScheduler.createUnitsSchedule(payload);
  }),
  getBacNetSchedules: thunk((actions, payload, { injections }) => {
    const { sdkScheduler } = injections;
    const { bacnetId, type } = payload;
    return sdkScheduler.getBacNetSchedules(bacnetId, type);
  }),
  createBacNetSchedule: thunk((actions, payload, { injections }) => {
    const { sdkScheduler } = injections;
    const { bacnetId, data } = payload;
    return sdkScheduler.createBacNetSchedule(bacnetId, data);
  }),
  updateBacNetSchedule: thunk((actions, payload, { injections }) => {
    const { sdkScheduler } = injections;
    const { id, data } = payload;
    return sdkScheduler.update(id, data);
  }),
  deleteBacNetSchedule: thunk((actions, payload, { injections }) => {
    const { sdkScheduler } = injections;
    return sdkScheduler.delete(payload);
  }),
  getModbusSchedules: thunk((actions, payload, { injections }) => {
    const { sdkScheduler } = injections;
    const { modbusId, type } = payload;
    return sdkScheduler.getModbusSchedules(modbusId, type);
  }),
  createModbusSchedule: thunk((actions, payload, { injections }) => {
    const { sdkScheduler } = injections;
    const { modbusId, data } = payload;
    return sdkScheduler.createModbusSchedule(modbusId, data);
  }),
};
